import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export type WebhookFilterOperator =
  | 'Equals'
  | 'IsNot'
  | 'StartsWith'
  | 'NotStartsWith'
  | 'EndsWith'
  | 'NotEndsWith'
  | 'Exists'
  | 'NotExists';

export const WEBHOOK_FILTER_OPERATORS: ReadonlyArray<WebhookFilterOperator> = [
  'Equals',
  'IsNot',
  'StartsWith',
  'NotStartsWith',
  'EndsWith',
  'NotEndsWith',
  'Exists',
  'NotExists'
];

export function webhookOperatorTakesValue(operator: WebhookFilterOperator): boolean {
  return operator !== 'Exists' && operator !== 'NotExists';
}

export interface WebhookFilter {
  FilterField: string;
  Operator: WebhookFilterOperator;
  Value?: string | null;
}

export interface WebhookFilterBlock {
  Description?: string | null;
  Filters: Array<WebhookFilter>;
}

export interface Webhook {
  Id: string;
  Name: string;
  Description: string;
  EventCodes: Array<string>;
  FilterBlocks: Array<WebhookFilterBlock>;
  Url: string;
  SecretId: string;
  IsEnabled: boolean;
  CategoryId?: string;
}

export type WebhookDto = Omit<Webhook, 'Id'>;

@Injectable({
  providedIn: 'root'
})
export class WebhookService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/v2/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getWebhooks(): Promise<Array<Webhook> | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/webhooks');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getWebhook(id: string): Promise<Webhook | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/webhooks/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateWebhook(id: string, webhook: WebhookDto): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/webhooks/${id}`, webhook);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createWebhook(webhook: WebhookDto): Promise<any | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/webhooks', webhook);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteWebhook(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/webhooks/${id}`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
